import React from 'react';
import { Plus, Shield, Phone, Clock, Trash2, Power } from 'lucide-react';

const AgentRoster = ({ 
  agents, 
  selectedAgent, 
  onAgentSelect, 
  onCreateNew, 
  onSetOnShift,
  onDelete,
  onEditAgent,
  loading 
}) => {
  const getStatusColor = (agent) => {
    if (agent?.is_currently_on_shift) return 'text-green-600 bg-green-50';
    switch (agent?.status) {
      case 'available': return 'text-blue-600 bg-blue-50';
      case 'off_duty': return 'text-gray-600 bg-gray-50';
      case 'vacation': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (agent) => {
    if (agent?.is_currently_on_shift) return 'On Shift';
    switch (agent?.status) {
      case 'available': return 'Available';
      case 'off_duty': return 'Off Duty';
      case 'vacation': return 'Vacation';
      default: return 'Unknown';
    }
  };

  const formatShiftTime = (shiftStarted) => {
    if (!shiftStarted) return '';
    const start = new Date(shiftStarted);
    const now = new Date();
    const diffHours = Math.floor((now - start) / (1000 * 60 * 60));
    const diffMins = Math.floor((now - start) / (1000 * 60)) % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  const handleSetOnShift = (agentId) => {
    onSetOnShift?.(agentId, true);
  };

  const handleDelete = (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to delete ${agentName}? This action cannot be undone.`)) {
      onDelete?.(agentId);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="border-b border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">Agent Roster</h2>
          <button
            onClick={onCreateNew}
            disabled={loading}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={16} />
            <span className="text-sm font-medium">Add Agent</span>
          </button>
        </div>
      </div>
      {/* Agent List */}
      <div className="p-6">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)]?.map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : agents?.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Agents Yet</h3>
            <p className="text-slate-600 mb-6">
              Get started by adding your first bail agent to the system.
            </p>
            <button
              onClick={onCreateNew}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Agent
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {agents?.map((agent) => (
              <div
                key={agent?.id}
                className={`relative group cursor-pointer border rounded-lg p-4 transition-all hover:shadow-md ${
                  selectedAgent?.id === agent?.id
                    ? 'border-blue-300 bg-blue-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                onClick={() => onAgentSelect?.(agent)}
              >
                <div className="flex items-start space-x-4">
                  {/* Profile Photo */}
                  <div className="flex-shrink-0">
                    {agent?.profile_photo_url ? (
                      <img
                        src={agent?.profile_photo_url}
                        alt={agent?.full_name}
                        className="w-12 h-12 rounded-full border-2 border-white shadow-sm object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                        <span className="text-white text-sm font-bold">
                          {agent?.full_name?.split(' ')?.map(n => n?.[0])?.join('')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Agent Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 truncate">
                          {agent?.full_name}
                        </h3>
                        <div className="flex items-center space-x-3 mt-1">
                          <div className="flex items-center space-x-1">
                            <Shield size={14} className="text-slate-500" />
                            <span className="text-sm text-slate-600">
                              {agent?.license_number}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Phone size={14} className="text-slate-500" />
                            <span className="text-sm text-slate-600">
                              {agent?.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(agent)}`}>
                        {getStatusText(agent)}
                      </div>
                    </div>

                    {/* Shift Time */}
                    {agent?.is_currently_on_shift && agent?.shift_started_at && (
                      <div className="flex items-center space-x-1 mt-2">
                        <Clock size={14} className="text-green-600" />
                        <span className="text-sm text-green-700 font-medium">
                          On duty for {formatShiftTime(agent?.shift_started_at)}
                        </span>
                      </div>
                    )}

                    {/* Specializations */}
                    {agent?.specializations?.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1">
                          {agent?.specializations?.slice(0, 3)?.map((spec, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700"
                            >
                              {spec}
                            </span>
                          ))}
                          {agent?.specializations?.length > 3 && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                              +{agent?.specializations?.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-2 mt-4 pt-3 border-t border-slate-100">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onEditAgent?.(agent);
                    }}
                    className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit</span>
                  </button>
                  
                  {!agent?.is_currently_on_shift && (
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleSetOnShift(agent?.id);
                      }}
                      className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      <Power size={14} />
                      <span>Set On Shift</span>
                    </button>
                  )}
                  
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleDelete(agent?.id, agent?.full_name);
                    }}
                    className="flex items-center space-x-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                  >
                    <Trash2 size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentRoster;